import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';

/**
 * CR-B3 — read-only audit trail of every yearGroup change on an
 * account (or on the childInfo sub-doc for parent accounts).
 *
 * Renders nothing when the history is empty — no visual noise on
 * accounts that have never been promoted.
 *
 * Props:
 *   currentYearGroup — the value being shown as "now" in the header
 *                      row (e.g. student.yearGroup or
 *                      parent.childInfo?.yearGroup).
 *   history          — the yearGroupHistory array from the account
 *                      (or from childInfo.yearGroupHistory).
 */
const fmtDate = (d) => {
    if (!d) return '';
    try {
        return new Date(d).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    } catch {
        return '';
    }
};

const YearGroupHistoryCard = ({ currentYearGroup, history }) => {
    if (!history || history.length === 0) return null;
    // Newest entry first — mongo stores in append order (oldest → newest).
    const rows = [...history].reverse();
    return (
        <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Year group history
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Automatic audit trail — every change to this account's year group is logged.
                    Historical progress reports keep the year group they were written in.
                </Typography>
                <Stack spacing={1.25}>
                    {rows.map((h, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 1,
                                px: 1.25,
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                                fontSize: '0.875rem',
                            }}
                        >
                            <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {h.from || '—'}
                            </Typography>
                            <ArrowRightAltRoundedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Typography component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {h.to || '—'}
                            </Typography>
                            <Typography component="span" sx={{ color: 'text.secondary', ml: 'auto' }}>
                                {fmtDate(h.at)}
                                {h.byAdminEmail ? ` · by ${h.byAdminEmail}` : ''}
                            </Typography>
                            {h.note && (
                                <Typography
                                    component="span"
                                    sx={{ color: 'text.secondary', fontStyle: 'italic', ml: 1 }}
                                >
                                    “{h.note}”
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Stack>
                {currentYearGroup && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                        Current: <strong>{currentYearGroup}</strong>
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default YearGroupHistoryCard;
